import * as con from "../Constants"
import { isNullOrUndefined, isNullOrUndefinedOrBlankOrEmpty } from './generalFunctions'
import * as XLSX from 'xlsx'

/**
 * Validates input data
 * @param {Array} resultsData - The results array from the annotation JSON
 * @returns {boolean} True if valid, false otherwise
 */
function validateInput(resultsData) {
    return resultsData && Array.isArray(resultsData);
}

/**
 * Creates base query information object
 * @param {Object} queryItem - Single query item from results
 * @returns {Object} Base query information
 */
function createBaseQueryInfo(queryItem) {
    return {
        [con.ARC_QUERY_ID]: queryItem[con.ARC_QUERY_ID] || con.MISSING_VALUE,
        [con.ARC_QUERY_LENGTH]: queryItem[con.ARC_QUERY_LENGTH] || con.MISSING_VALUE,
        [con.ARC_HIT_COUNT]: queryItem[con.ARC_HIT_COUNT] || 0
    };
}

/**
 * Processes taxonomy object into separate columns
 * @param {Object} taxonomyObject - Taxonomy object from hit
 * @param {string} prefix - Optional prefix for column names (e.g., "1_" for wide format)
 * @returns {Object} Object with separate taxonomy columns
 */
function processTaxonomy(taxonomyObject, prefix = "") {
    const taxonomyColumns = {};
    
    if (!isNullOrUndefined(taxonomyObject)) {
        con.TAXONOMIC_LEVELS.forEach(level => {
            const value = taxonomyObject[level];
            taxonomyColumns[`${prefix}${level}`] = (!isNullOrUndefined(value) && value !== "") ? value : con.MISSING_VALUE;
        });
    } else {
        con.TAXONOMIC_LEVELS.forEach(level => {
            taxonomyColumns[`${prefix}${level}`] = con.MISSING_VALUE;
        });
    }
    
    return taxonomyColumns;
}

/**
 * Processes a hit object and extracts relevant fields
 * @param {Object} hit - Hit object from results
 * @param {string} prefix - Optional prefix for column names
 * @returns {Object} Processed hit data
 */
function processHit(hit, prefix = "") {
    const hitData = {};
    
    // Add all hit properties except alignment data and sseqid
    Object.keys(hit).forEach(key => {
        if (key !== con.ARC_ALIGNMENT && key !== con.ARC_SSQID && key !== con.ARC_TAXONOMY) {
            hitData[`${prefix}${key}`] = hit[key];
        }
    });
    
    // Process taxonomy into separate columns
    const taxonomyColumns = processTaxonomy(hit[con.ARC_TAXONOMY], prefix);
    Object.assign(hitData, taxonomyColumns);
    
    return hitData;
}

/**
 * Prepares tabular data with only the best match (first hit) for each query sequence
 * @param {Array} resultsData - The results array from the annotation JSON
 * @returns {Array} Array of objects with flattened best hit data
 */
export function exportTabBestHits(resultsData) {
    if (!validateInput(resultsData)) {
        return [];
    }

    return resultsData.map(item => {
        const row = createBaseQueryInfo(item);

        // Add data from the first hit if available
        if (!isNullOrUndefinedOrBlankOrEmpty(item[con.ARC_HITS]) && item[con.ARC_HITS].length > 0) {
            const firstHit = item[con.ARC_HITS][0];
            const hitData = processHit(firstHit);
            Object.assign(row, hitData);
        }

        return row;
    });
}

/**
 * Prepares tabular data in long format with one row per hit
 * @param {Array} resultsData - The results array from the annotation JSON
 * @returns {Array} Array of objects with one row per hit
 */
export function exportTabLong(resultsData) {
    if (!validateInput(resultsData)) {
        return [];
    }

    const longFormatData = [];

    resultsData.forEach(item => {
        const baseInfo = createBaseQueryInfo(item);

        if (!isNullOrUndefinedOrBlankOrEmpty(item[con.ARC_HITS])) {
            item[con.ARC_HITS].forEach((hit, hitIndex) => {
                const row = { ...baseInfo };
                
                // Add hit index (1-based)
                row.hit_index = hitIndex + 1;
                
                // Process hit data
                const hitData = processHit(hit);
                Object.assign(row, hitData);

                longFormatData.push(row);
            });
        } else {
            // If no hits, still add the query info
            longFormatData.push(baseInfo);
        }
    });

    return longFormatData;
}

/**
 * Prepares tabular data in wide format with one row per query and numbered columns for each hit
 * @param {Array} resultsData - The results array from the annotation JSON
 * @returns {Array} Array of objects with one row per query and numbered hit columns
 */
export function exportTabWide(resultsData) {
    if (!validateInput(resultsData)) {
        return [];
    }

    // First, determine the maximum number of hits across all queries
    const hitCounts = resultsData.map(item => 
        item[con.ARC_HITS] ? item[con.ARC_HITS].length : 0
    );
    const maxHits = hitCounts.length > 0 ? Math.max(...hitCounts) : 0;

    return resultsData.map(item => {
        const row = createBaseQueryInfo(item);

        if (!isNullOrUndefinedOrBlankOrEmpty(item[con.ARC_HITS])) {
            item[con.ARC_HITS].forEach((hit, hitIndex) => {
                const hitNumber = hitIndex + 1;
                const prefix = `${hitNumber}_`;
                
                // Process hit data with numbered prefix
                const hitData = processHit(hit, prefix);
                Object.assign(row, hitData);
            });
        }

        // Fill remaining hit columns with empty values for consistency (only if maxHits > 0)
        if (maxHits > 0) {
            const currentHitCount = item[con.ARC_HITS] ? item[con.ARC_HITS].length : 0;
            for (let i = currentHitCount + 1; i <= maxHits; i++) {
                const prefix = `${i}_`;
                
                // Get all possible hit fields by processing a sample hit or using known fields
                const commonHitFields = [
                    con.ARC_PIDENT, con.ARC_QCOVS, con.ARC_EVALUE, con.ARC_BITSCORE, 
                    con.ARC_QSTART, con.ARC_QEND, con.ARC_SSTRAND, con.ARC_TARGET_LENGTH,
                    con.ARC_LENGTH, con.ARC_MISMATCH, con.ARC_GAPOPEN, con.ARC_SSTART, con.ARC_SEND
                ];
                
                // Add empty columns for missing hits
                commonHitFields.forEach(field => {
                    row[`${prefix}${field}`] = "";
                });
                
                // Add empty taxonomy columns
                con.TAXONOMIC_LEVELS.forEach(level => {
                    row[`${prefix}${level}`] = "";
                });
            }
        }

        return row;
    });
}

/**
 * Creates a legend table with variable descriptions for export data
 * @returns {Array} Array of objects with Variable and Description columns
 */
export function exportLegend() {
    return [
        // Query-level variables
        {
            Variable: con.ARC_QUERY_ID,
            Description: "Query sequence identifier"
        },
        {
            Variable: con.ARC_QUERY_LENGTH,
            Description: "Length of the query sequence in nucleotides"
        },
        {
            Variable: con.ARC_HIT_COUNT,
            Description: "Number of hits found for this query"
        },
        {
            Variable: "hit_index",
            Description: "Position of hit (1st, 2nd, etc.) - only in long format"
        },
        
        // Hit-level alignment variables
        {
            Variable: con.ARC_PIDENT,
            Description: "Sequence identity percentage"
        },
        {
            Variable: con.ARC_LENGTH,
            Description: "Length of the alignment in nucleotides"
        },
        {
            Variable: con.ARC_MISMATCH,
            Description: "Number of mismatched positions"
        },
        {
            Variable: con.ARC_GAPOPEN,
            Description: "Number of gap openings in the alignment"
        },
        {
            Variable: con.ARC_QCOVS,
            Description: "Percentage of query covered by alignment"
        },
        {
            Variable: con.ARC_EVALUE,
            Description: "E-value (not available for VSEARCH output)"
        },
        {
            Variable: con.ARC_BITSCORE,
            Description: "Bit score (for VSEARCH, raw score is used)"
        },
        {
            Variable: con.ARC_QSTART,
            Description: "Start position in query sequence"
        },
        {
            Variable: con.ARC_QEND,
            Description: "End position in query sequence"
        },
        {
            Variable: con.ARC_SSTART,
            Description: "Start position in subject/target sequence"
        },
        {
            Variable: con.ARC_SEND,
            Description: "End position in subject/target sequence"
        },
        {
            Variable: con.ARC_SSTRAND,
            Description: "Alignment strand (e.g., 'plus' or 'minus')"
        },
        {
            Variable: con.ARC_TARGET_LENGTH,
            Description: "Length of the subject sequence in nucleotides"
        },
        
        // Taxonomic classification variables
        {
            Variable: con.KINGDOM,
            Description: "Taxonomic kingdom"
        },
        {
            Variable: con.PHYLUM,
            Description: "Taxonomic phylum"
        },
        {
            Variable: con.CLASS,
            Description: "Taxonomic class"
        },
        {
            Variable: con.ORDER,
            Description: "Taxonomic order"
        },
        {
            Variable: con.FAMILY,
            Description: "Taxonomic family"
        },
        {
            Variable: con.GENUS,
            Description: "Taxonomic genus"
        },
        {
            Variable: con.SPECIES,
            Description: "Taxonomic species"
        }
    ];
}


/**
 * Creates Excel export
 */
export function createExcelExport(resultsData, options = {}) {
    
    const {
        filename = 'EUKARYOME_results',
        includeBestHits = true,
        includeLongFormat = true,
        includeWideFormat = true,
        includeLegend = true,
        metadata = {}
    } = options;

    try {
        const workbook = XLSX.utils.book_new();
        
        // Optional: Add metadata sheet
        if (Object.keys(metadata).length > 0) {
            const metadataArray = Object.entries(metadata).map(([key, value]) => ({
                Parameter: key,
                Value: value
            }));
            const metadataSheet = XLSX.utils.json_to_sheet(metadataArray);
            // Set column widths
            metadataSheet['!cols'] = [{ wch: 30 }, { wch: 35 }];
            XLSX.utils.book_append_sheet(workbook, metadataSheet, 'metadata');
        }
        
        // Add sheets based on options
        if (includeBestHits) {
            const bestHitsData = exportTabBestHits(resultsData);
            if (bestHitsData.length > 0) {
                const sheet = XLSX.utils.json_to_sheet(bestHitsData);
                // Set column widths
                sheet['!cols'] = Object.keys(bestHitsData[0]).map(() => ({ wch: 15 }));
                XLSX.utils.book_append_sheet(workbook, sheet, 'best_hits');
            }
        }
        
        if (includeLongFormat) {
            const longData = exportTabLong(resultsData);
            if (longData.length > 0) {
                const sheet = XLSX.utils.json_to_sheet(longData);
                XLSX.utils.book_append_sheet(workbook, sheet, 'all_hits_long');
            }
        }
        
        if (includeWideFormat) {
            const wideData = exportTabWide(resultsData);
            if (wideData.length > 0) {
                const sheet = XLSX.utils.json_to_sheet(wideData);
                XLSX.utils.book_append_sheet(workbook, sheet, 'all_hits_wide');
            }
        }
        
        if (includeLegend) {
            const legendData = exportLegend();
            const sheet = XLSX.utils.json_to_sheet(legendData);
            // Make legend columns wider for readability
            sheet['!cols'] = [{ wch: 20 }, { wch: 50 }];
            XLSX.utils.book_append_sheet(workbook, sheet, 'legend');
        }
        
        // Generate the Excel file as a blob and return it
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        
        // console.log(`Excel file created with sheets:`, workbook.SheetNames);
        
        return { blob, filename: `${filename}.xlsx` };
        
    } catch (error) {
        console.error('Error creating Excel file:', error);
        throw error;
    }
}
